import { deleteThingModel, addThingModel, ThingModelItem } from '@/api';
import useAuthButton from '@/hooks/useAuthButton';
import { httpRequest } from '@/utils/request';
import { ExportOutlined, ImportOutlined, PlusOutlined, ThunderboltOutlined, AlertOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, message, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { get } from 'lodash';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import DataModal, { DataModalRef } from './DataModal';
import { DetailModal, DetailModalRef } from './DeatilModal';
import { TypeSpecMap } from './helper';
import { SolutionCenterModal, SolutionCenterModalRef } from './SolutionCenterModal';
import { ProductActions, ProductEvents, ProductInfo, ProductProperties, ProductThingModelType, TypeSpec } from './types';
import { getUserGetinfo } from '@/api/system/user';

const { Text, Link } = Typography;
const { useForm, useWatch } = Form;

const PROVIDER_ROLE_KEY = 'provider';

/** 标准物模型（物联网水控机 — 23个属性） */
const STANDARD_PROPERTIES: Array<{
  name: string;
  code: string;
  type: string;
  access_mode: string;
  specs: Record<string, any>;
  description?: string;
}> = [
  // ── 基础数据 ──
  { name: '数据ID', code: 'frame_id', type: 'int', access_mode: 'R', description: '仅为观察数据用', specs: { min: '0', max: '999999', step: '1' } },
  { name: '设备经度', code: 'longitude', type: 'float', access_mode: 'R', description: '经纬度需显示可从本地服务获取', specs: { min: '-180', max: '180', step: '0.000001', length: '9' } },
  { name: '设备纬度', code: 'latitude', type: 'float', access_mode: 'R', description: '设备安装位置纬度', specs: { min: '-90', max: '90', step: '0.000001', length: '8' } },
  // ── 传感器测量 ──
  { name: '流量', code: 'flow_rate', type: 'float', access_mode: 'R', description: '流水流量测量', specs: { min: '0', max: '6', step: '0.01', unit: 'L/Min', unitName: '升/分钟' } },
  { name: '温度', code: 'temperature', type: 'float', access_mode: 'R', description: '水温测量', specs: { min: '0', max: '50', step: '0.1', unit: '℃', unitName: '摄氏度' } },
  { name: 'TDS', code: 'tds', type: 'int', access_mode: 'R', description: 'TDS水质检测值', specs: { min: '0', max: '1000', step: '1', unit: 'PPM', unitName: 'PPM' } },
  { name: '电池电压', code: 'battery_voltage', type: 'float', access_mode: 'R', description: '电池电压', specs: { min: '2', max: '4', step: '0.01', unit: 'V', unitName: '伏特' } },
  { name: '电池电量', code: 'battery_percent', type: 'float', access_mode: 'R', description: '电池电量百分比', specs: { min: '0', max: '100', step: '0.01', unit: '%', unitName: '百分比' } },
  // ── 控制类 ──
  { name: '电磁阀控制', code: 'valve_onoff_control', type: 'bool', access_mode: 'RW', description: '由设备上报给平台下发', specs: { '0': '关', '1': '开' } },
	{ name: '设备开关机(预留功能暂未使用)', code: 'onoff_control', type: 'bool', access_mode: 'RW', description: '预留功能暂不使用', specs: { '1': '开', '2': '关' } },
  // ── 滤芯时间戳 ──
  { name: 'PP棉滤芯时间', code: 'PPM_time', type: 'date', access_mode: 'RW', description: 'PP棉滤芯安装时间，选装可按需配置，默认6个月', specs: {} },
  { name: '超滤膜芯更换时间', code: 'CLM_time', type: 'date', access_mode: 'RW', description: '超滤膜滤芯安装时间，12个月到期', specs: {} },
  { name: 'CM压缩活性炭芯更换时间', code: 'CMYSHXT_time', type: 'date', access_mode: 'RW', description: 'CM压缩活性炭滤芯安装时间，6个月到期', specs: {} },
  // ── 二维码 ──
  { name: '净水机二维码管理', code: 'QR_code', type: 'text', access_mode: 'RW', description: '每台设备上粘贴一张二维码', specs: { length: '255' } },
	{ name: '4g卡-ICCID', code: 'iccid', type: 'text', access_mode: 'R', description: '唯一身份标识，不可更改', specs: { length: '255' } },
  // ── 租赁计费 ──
  { name: '流量租赁余额', code: 'flow_rent_quota', type: 'float', access_mode: 'RW', description: '流量充值后设备开始为客户计费', specs: { min: '0', max: '999999', step: '0.01', unit: 'L', unitName: '升' } },
  { name: '时长租赁余额', code: 'time_rent_quota', type: 'date', access_mode: 'RW', description: '时长为倒计时，初始360天×24小时×60分', specs: {} },
  { name: '租赁流量费用比例', code: 'rent_flow_proportion', type: 'float', access_mode: 'RW', description: '流量充值费用比例', specs: { min: '0', max: '10000', step: '0.01', unit: '%', unitName: '百分比' } },
  { name: '租赁时长费用比例', code: 'rent_time_proportion', type: 'float', access_mode: 'RW', description: '时长充值费用比例', specs: { min: '0', max: '10000', step: '0.01', unit: '%', unitName: '百分比' } },
  // ── 防偷报警 ──
  { name: '防偷功能_位置偏移', code: 'anti_theft_postion_change', type: 'bool', access_mode: 'R', description: '判断装置位置作为防偷触发点', specs: { '0': '正常', '1': '异常' } },
  { name: '防偷功能_无流量TDS值', code: 'anti_theft_no_flow_tds', type: 'bool', access_mode: 'R', description: '长时间无流量和高TDS值时报警', specs: { '0': '正常', '1': '异常' } },
  { name: '防偷功能_断电报警', code: 'anti_theft_off_long_time', type: 'bool', access_mode: 'R', description: '断电后当设备未恢复时报警', specs: { '0': '正常', '1': '异常' } },
  // ── 其他 ──
  { name: '本地服务电话', code: 'Local_service_phone_number', type: 'text', access_mode: 'RW', description: '显示本地服务商电话', specs: { length: '20' } },
];

/** 标准告警规则 */
const ALERT_RULES: Array<{
  name: string;
  code: string;
  alert_level: string;
  description: string;
  option: Record<string, string>;
}> = [
  // 传感器测量类
  { name: '流量超限报警', code: 'flow_rate', alert_level: '重要', description: '流量超过0.5L/min时触发报警', option: { value_type: 'original', decide_condition: '> 0.5' } },
  { name: '温度过高报警', code: 'temperature', alert_level: '紧急', description: '水温超过80℃时触发高温报警', option: { value_type: 'original', decide_condition: '> 80' } },
  { name: 'TDS水质超标报警', code: 'tds', alert_level: '重要', description: 'TDS值超过500mg/L时触发', option: { value_type: 'original', decide_condition: '> 500' } },
  { name: '电池电压过低报警', code: 'battery_voltage', alert_level: '提示', description: '电池电压低于2.8V', option: { value_type: 'original', decide_condition: '< 2.8' } },
  { name: '电池电量不足报警', code: 'battery_percent', alert_level: '重要', description: '电池电量低于20%', option: { value_type: 'original', decide_condition: '< 20' } },
  // 控制类
  { name: '电磁阀断电报警', code: 'valve_onoff_control', alert_level: '紧急', description: '净水机断电后电磁阀异常关闭', option: { decide_condition: '= false' } },
  // 防偷防盗类
  { name: '防偷-位置偏移报警', code: 'anti_theft_postion_change', alert_level: '紧急', description: '设备位置发生偏移', option: { decide_condition: '= true' } },
  { name: '防偷-无流量高TDS报警', code: 'anti_theft_no_flow_tds', alert_level: '重要', description: '长时间无用水且TDS值异常偏高', option: { decide_condition: '= true' } },
  { name: '防偷-断电未恢复报警', code: 'anti_theft_off_long_time', alert_level: '紧急', description: '设备断电后长时间未恢复上线', option: { decide_condition: '= true' } },
  // PP棉滤芯
  { name: 'PP棉滤芯-1月到期提醒', code: 'PPM_time', alert_level: '提示', description: 'PP棉滤芯距到期还有约1个月', option: { value_type: 'date_expiry', decide_condition: '> 5184000000' } },
  { name: 'PP棉滤芯-10天到期警告', code: 'PPM_time', alert_level: '重要', description: 'PP棉滤芯距到期还有约10天', option: { value_type: 'date_expiry', decide_condition: '> 6912000000' } },
  { name: 'PP棉滤芯-3天到期紧急', code: 'PPM_time', alert_level: '紧急', description: 'PP棉滤芯仅剩约3天有效期！', option: { value_type: 'date_expiry', decide_condition: '> 7516800000' } },
  { name: 'PP棉滤芯-到期自动关阀', code: 'PPM_time', alert_level: '紧急', description: 'PP棉滤芯已到期，系统自动关闭电磁阀', option: { value_type: 'date_expiry', decide_condition: '> 7776000000' } },
  // 超滤膜芯
  { name: '超滤膜芯-1月到期提醒', code: 'CLM_time', alert_level: '提示', description: '超滤膜芯距到期还有约1个月', option: { value_type: 'date_expiry', decide_condition: '> 28944000000' } },
  { name: '超滤膜芯-10天到期警告', code: 'CLM_time', alert_level: '重要', description: '超滤膜芯距到期还有约10天', option: { value_type: 'date_expiry', decide_condition: '> 30662400000' } },
  { name: '超滤膜芯-3天到期紧急', code: 'CLM_time', alert_level: '紧急', description: '超滤膜芯仅剩约3天有效期！', option: { value_type: 'date_expiry', decide_condition: '> 31276800000' } },
  { name: '超滤膜芯-到期自动关阀', code: 'CLM_time', alert_level: '紧急', description: '超滤膜芯已到期，系统自动关闭电磁阀', option: { value_type: 'date_expiry', decide_condition: '> 31536000000' } },
  // 活性炭芯
  { name: '活性炭芯-1月到期提醒', code: 'CMYSHXT_time', alert_level: '提示', description: '活性炭芯距到期还有约1个月', option: { value_type: 'date_expiry', decide_condition: '> 12960000000' } },
  { name: '活性炭芯-10天到期警告', code: 'CMYSHXT_time', alert_level: '重要', description: '活性炭芯距到期还有约10天', option: { value_type: 'date_expiry', decide_condition: '> 14688000000' } },
  { name: '活性炭芯-3天到期紧急', code: 'CMYSHXT_time', alert_level: '紧急', description: '活性炭芯仅剩约3天有效期！', option: { value_type: 'date_expiry', decide_condition: '> 15292800000' } },
  { name: '活性炭芯-到期自动关阀', code: 'CMYSHXT_time', alert_level: '紧急', description: '活性炭芯已到期，系统自动关闭电磁阀', option: { value_type: 'date_expiry', decide_condition: '> 15552000000' } },
  // 流量租赁
  { name: '流量租赁-余额不足提醒', code: 'flow_rent_quota', alert_level: '重要', description: '流量租赁余额不足10%', option: { value_type: 'original', decide_condition: '<= 100' } },
  { name: '流量租赁-耗尽自动停供', code: 'flow_rent_quota', alert_level: '紧急', description: '流量租赁余额已耗尽', option: { value_type: 'original', decide_condition: '<= 0' } },
  // 时长租赁
  { name: '时长租赁-即将到期提醒', code: 'time_rent_quota', alert_level: '重要', description: '时长租赁距到期还有30天', option: { value_type: 'date_expiry', decide_condition: '> 2592000000' } },
  { name: '时长租赁-到期自动停供', code: 'time_rent_quota', alert_level: '紧急', description: '时长租赁已到期', option: { value_type: 'date_expiry', decide_condition: '> 0' } },
];

const TypeMap: { [key: string]: string } = {
  action: '服务类型',
  event: '事件类型',
  property: '属性类型',
};

interface DefaultDetailProps {
  productInfo: ProductInfo | undefined;
  isCloudProduct: boolean;
  loadProductInfo: () => void;
  productId: string;
}

export const DefaultDetail: React.FC<DefaultDetailProps> = ({ productInfo, isCloudProduct, loadProductInfo, productId }) => {
  // _BUILD_TEST_MARKER_20260609_
  const [importing, setImporting] = useState(false);
  const [alertImporting, setAlertImporting] = useState(false);
  const [form] = useForm();
  const nameValue = useWatch('name', form);
  const typeValue = useWatch('type', form);
  const dataModalRef = useRef<DataModalRef>(null);
  const detailModalRef = useRef<DetailModalRef>(null);
  const solutionCenterModalRef = useRef<SolutionCenterModalRef>(null);
  const { checkPermissionDisabled } = useAuthButton();

  const handleImport = async () => {
    if (!productId) {
      message.error('产品ID不存在，无法导入标准物模型');
      return;
    }
    console.log('[一键导入] 开始导入标准物模型, productId:', productId, '共', STANDARD_PROPERTIES.length, '个属性');
    setImporting(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const prop of STANDARD_PROPERTIES) {
        try {
          await addThingModel({
            product_id: productId,
            name: prop.name,
            code: prop.code,
            description: prop.description || '',
            thing_model_type: 'property',
            tag: '',
            property: {
              access_model: prop.access_mode,
              require: false,
              type: prop.type,
              specs: JSON.stringify(prop.specs),
            },
            sort: 1,
            storage_mode: 1,
            external: {},
          });
          successCount++;
          console.log('[一键导入] 属性导入成功:', prop.name, '(' + prop.code + ')');
        } catch (e) {
          console.warn('[一键导入] 属性导入失败:', prop.name, e);
          failCount++;
        }
      }

      if (successCount > 0 && failCount === 0) {
        message.success(`成功导入 ${successCount} 个标准物模型属性`);
      } else if (successCount > 0) {
        message.warning(`已导入 ${successCount} 个属性，${failCount} 个导入失败`);
      } else {
        message.error('所有物模型属性导入失败');
      }

      if (successCount > 0) {
        message.success(`成功导入 ${successCount} 个属性，即将刷新页面...`);
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (e) {
      console.error('[一键导入] 异常:', e);
      message.error('导入标准物 model异常，请重试');
    }

    setImporting(false);
  };

  const handleAlertImport = async () => {
    if (!productId) {
      message.error('产品ID不存在，无法导入告警规则');
      return;
    }
    console.log('[告警导入] 开始导入, productId:', productId, '共', ALERT_RULES.length, '条规则');
    setAlertImporting(true);

    try {
      // 第0步：检查产品是否有物模型属性（告警规则需要关联物模型code）
      if (productInfo && (!productInfo.properties || productInfo.properties.length === 0)) {
        message.warning('该产品暂无物模型属性，请先点击"一键导入标准物模型"后再导入告警规则');
        setAlertImporting(false);
        return;
      }
      console.log('[告警导入] 物模型属性数量:', productInfo?.properties?.length);

      // 第0.5步：刷新产品信息，确保物模型数据是最新的（避免刚导入属性后缓存不一致）
      loadProductInfo();
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 第一步：查询该产品下的所有设备ID
      const deviceResp = await httpRequest({
        method: 'GET',
        url: `/api/v1/devices?product_id=${productId}&pageSize=1000`,
      });
      const deviceData = deviceResp as any;
      const deviceIds: string[] = (deviceData?.result?.list || []).map((d: { id: string }) => d.id);

      if (deviceIds.length === 0) {
        message.warning('该产品下暂无设备，请先添加设备后再导入告警规则');
        setAlertImporting(false);
        return;
      }
      console.log('[告警导入] 找到设备:', deviceIds.length, '台', 'IDs:', JSON.stringify(deviceIds));

      // 第二步：查询所有已有规则（用于同名覆盖，分页查询确保不遗漏）
      const allExistingRules: Array<{ id: string; name: string }> = [];
      for (let page = 1; ; page++) {
        const searchResp = await httpRequest({
            method: 'GET',
            url: `/api/v1/alert-rule?page=${page}&pageSize=100&productId=${productId}`,
          });
        const searchData = searchResp as any;
        const list: Array<{ id: string; name: string }> = searchData?.result?.list || searchData?.result || [];
        if (list.length === 0) break;
        allExistingRules.push(...list);
        if (list.length < 100) break;
      }
      const existingMap = new Map(allExistingRules.map((r: { id: string; name: string }) => [r.name, r.id]));
      console.log('[告警导入] 已有规则:', allExistingRules.length, '条', '名称列表:', allExistingRules.map((r: { name: string }) => r.name));

      let createSuccessCount = 0;
      let configFailCount = 0;
      const configuredRuleIds: Array<{ id: string; name: string }> = [];

      // ════════════ 阶段一：创建+配置所有规则 ════════════
      console.log('[告警导入] ===== 阶段一：创建并配置所有规则 =====');
      for (const rule of ALERT_RULES) {
        try {
          let ruleId: string | undefined;

          // 同名覆盖：如果已存在同名规则，先删除再创建
          if (existingMap.has(rule.name)) {
            const oldRuleId = existingMap.get(rule.name)!;
            console.log('[告警导入] 发现同名规则，删除旧规则:', rule.name, 'oldId:', oldRuleId);
            try {
              await httpRequest({ method: 'DELETE', url: `/api/v1/alert-rule/${oldRuleId}` });
              console.log('[告警导入] 旧规则已删除:', rule.name);
            } catch (delErr) {
              console.warn('[告警导入] 删除旧规则失败(非致命):', rule.name, delErr);
            }
          }

          // Step A: 创建新规则骨架
          const createResp = await httpRequest({
            method: 'POST',
            url: '/api/v1/alert-rule',
            data: {
              name: rule.name,
              alert_type: '设备告警',
              alert_level: rule.alert_level,
              description: rule.description,
              product_id: productId,
            },
          });
          const createData = createResp as any;
          if (!(createData?.success === true || createData?.errorCode === 0)) {
            console.error('[告警导入] 创建失败:', rule.name, createData);
            configFailCount++;
            continue;
          }

          ruleId = createData?.result || createData?.id;
          if (!ruleId || typeof ruleId !== 'string' || (Array.isArray(ruleId) && ruleId.length === 0)) {
            const reSearchResp = await httpRequest({
              method: 'GET',
              url: `/api/v1/alert-rule?page=1&pageSize=5&productId=${productId}`,
            });
            const reSearchData = reSearchResp as any;
            const reList = reSearchData?.result?.list || [];
            const found = reList.find((r: { name: string; id: string }) => r.name === rule.name);
            if (found?.id) ruleId = found.id;
          }

          if (!ruleId || typeof ruleId !== 'string') {
            console.error('[告警导入] 无法获取ruleId:', rule.name);
            configFailCount++;
            continue;
          }

          // Step B: 配置子规则（触发条件、设备绑定）
          const updatePayload = {
            id: ruleId,
            condition: 'anyone',
            sub_rule: [{
              trigger: '设备数据触发',
              product_id: productId,
              device_ids: deviceIds,
              option: {
                code: rule.code,
                ...rule.option,
              },
            }],
            silence_time: 300,
            notify: [],
          };

          const updateResp = await httpRequest({
            method: 'PUT',
            url: `/api/v1/alert-rule/${encodeURIComponent(ruleId)}`,
            data: updatePayload,
          });

          const updateData = updateResp as any;
          if (updateData?.success === true || updateData?.errorCode === 0) {
            createSuccessCount++;
            configuredRuleIds.push({ id: ruleId, name: rule.name });
            console.log('[告警导入] 规则就绪:', rule.name, '→ ruleId:', ruleId);
          } else {
            console.error('[告警导入] 配置失败:', rule.name, updateData);
            try { await httpRequest({ method: 'DELETE', url: `/api/v1/alert-rule/${ruleId}` }); } catch (_) {}
            configFailCount++;
          }
        } catch (e) {
          console.error('[告警导入] 异常:', rule.name, e);
          configFailCount++;
        }
      }

      // ════════════ 阶段二：等待后端缓存刷新后批量启用 ════════════
      let startSuccessCount = 0;
      let startFailCount = 0;

      if (configuredRuleIds.length > 0) {
        console.log('[告警导入] ===== 等待5秒后批量启用', configuredRuleIds.length, '条规则 =====');
        await new Promise(resolve => setTimeout(resolve, 5000));
        loadProductInfo();

        for (const { id: ruleId, name: ruleName } of configuredRuleIds) {
          try {
            const startResp = await httpRequest({
              method: 'POST',
              url: `/api/v1/alert-rule/${encodeURIComponent(ruleId)}/start`,
            }) as any;
            if (startResp?.success === true || startResp?.errorCode === 0) {
              startSuccessCount++;
            } else {
              console.warn('[告警导入] 启用失败:', ruleName, startResp?.errorMsg);
              startFailCount++;
            }
          } catch (startErr) {
            console.warn('[告警导入] 启用异常:', ruleName, startErr);
            startFailCount++;
          }
        }
      }

      if (startSuccessCount === configuredRuleIds.length) {
        message.success(`成功导入并启用 ${startSuccessCount} 条告警规则`);
      } else if (createSuccessCount > 0) {
        message.warning(
          `已创建 ${createSuccessCount} 条规则，启用 ${startSuccessCount} 条` +
          (startFailCount > 0 ? `，${startFailCount} 条需手动启用` : ''),
        );
      } else {
        message.error('所有告警规则导入失败');
      }
    } catch (e) {
      console.error('[告警导入] 异常:', e);
      message.error('导入告警规则异常，请重试');
    }

    setAlertImporting(false);
  };

  const dataSource = useMemo(() => {
    if (!productInfo) return [];
    const list: Array<ProductThingModelType & { key?: string }> = [
      ...(productInfo.properties || []).map((item) => ({ ...item, type: 'property' as const })),
      ...(productInfo.events || []).map((item) => ({ ...item, type: 'event' as const })),
      ...(productInfo.actions || []).map((item) => ({ ...item, type: 'action' as const })),
    ];
    return list.filter(({ type, name }) => {
      return type.includes(typeValue || '') && name.includes(nameValue || '');
    });
  }, [productInfo, nameValue, typeValue]);

  const columns = [
    {
      title: '功能类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value: string) => {
        return (
          <>
            {value === 'property' && <Tag color="green">{TypeMap[value]}</Tag>}
            {value === 'action' && <Tag color="blue">{TypeMap[value]}</Tag>}
            {value === 'event' && <Tag color="volcano">{TypeMap[value]}</Tag>}
          </>
        );
      },
    },
    {
      title: '功能名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '标识符',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: '数据类型',
      key: 'type_spec_type',
      render: (_: any, record: ProductThingModelType) => {
        const type = get(record, 'type_spec.type', '') as string;
        return TypeSpecMap[type as keyof typeof TypeSpecMap] || '-';
      },
    },
    {
      title: '读写类型',
      dataIndex: 'access_mode',
      key: 'access_mode',
      width: 140,
      render: (value: string) => {
        const map: { [key: string]: string } = {
          RW: '可下发可上报（RW）',
          R: '只上报（R）',
          W: '只下发（W）',
        };
        return map[value] || value || '-';
      },
    },
    {
      title: '数据定义',
      key: 'type_spec_specs',
      render: (_: any, record: ProductThingModelType) => {
        const type = get(record, 'type_spec.type', '') as string;
        const specsStr = get(record, 'type_spec.specs', '{}') as string;
        let specs: any;
        try {
          specs = typeof specsStr === 'string' ? JSON.parse(specsStr) : specsStr;
        } catch {
          return '-';
        }

        if (type === 'array') {
          return '数组个数: ' + specs?.size;
        } else if (type === 'text') {
          return '数据长度: ' + specs?.length;
        } else if (type === 'date') {
          return 'Unix时间戳 (秒), 用于倒计时计算';
        } else if (type === 'bool' || type === 'enum') {
          return Object.entries(specs)
            .map(([k, v]) => `${k}-${v}`)
            .map((text, index) => (
              <Tag color="processing" key={index}>
                {text}
              </Tag>
            ));
        } else if (type === 'int' || type === 'float') {
          return (
            <>
              <div>
                取值范围: {specs?.min}-{specs?.max}
              </div>
              <div>步长: {specs?.step}</div>
              <div>
                单位: {specs?.unit} ({specs?.unitName})
              </div>
            </>
          );
        }
        return '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 160,
      render: (_: any, record: ProductThingModelType) => (
        <Space split={<Text type="secondary">/</Text>}>
          <Link
            onClick={() => {
              detailModalRef.current?.open(record);
            }}
          >
            查看
          </Link>
          <Link
            onClick={() => {
              detailModalRef.current?.open(record, true);
            }}
          >
            编辑
          </Link>
          <Popconfirm
            title="此操作将永久删除该记录, 是否继续?"
            okText="删除"
            cancelText="取消"
            placement="topRight"
            disabled={checkPermissionDisabled('gateway:product:tsl:remove')}
            onConfirm={async () => {
              try {
                const res = await deleteThingModel({
                  thing_model_id: record.id,
                  thing_model_type: record.type,
                });
                if (res.success) {
                  message.success('删除成功');
                  loadProductInfo();
                } else {
                  message.error(res?.errorMsg);
                }
              } catch (e) {
                message.error('删除失败');
              }
            }}
          >
            <Link disabled={checkPermissionDisabled('gateway:product:tsl:remove')}>删除</Link>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="m20">
      <span style={{display:'none'}}>DEPLOY_TEST_V2_20260609_0130_OK</span>
      <div className="mb15" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Form layout="inline" form={form} initialValues={{ type: '', name: '' }} className="mb0">
          <Form.Item name="type" className="mb0" style={{ marginBottom: 0 }}>
            <Select
              placeholder="功能类型 (全部)"
              style={{ minWidth: 130 }}
              allowClear
              size="middle"
              options={[
                { label: '属性', value: 'property' },
                { label: '事件', value: 'event' },
                { label: '服务', value: 'action' },
              ]}
            />
          </Form.Item>
          <Form.Item name="name" className="mb0" style={{ marginBottom: 0 }}>
            <Input placeholder="请输入产品名称搜索" style={{ width: 180 }} allowClear size="middle" />
          </Form.Item>
        </Form>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <Button
            type="primary"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => detailModalRef.current?.open(undefined, true)}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            添加
          </Button>
          <Button
            size="middle"
            icon={<ThunderboltOutlined />}
            onClick={handleImport}
            loading={importing}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            一键导入标准物模型
          </Button>
          <Button
            size="middle"
            icon={<AlertOutlined />}
            onClick={handleAlertImport}
            loading={alertImporting}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            导入告警规则
          </Button>
          <Button
            size="middle"
            icon={<ImportOutlined />}
            onClick={() => productId && dataModalRef.current?.open(productId, 'import')}
            disabled={checkPermissionDisabled('gateway:product:tsl:import')}
          >
            导入
          </Button>
          <Button
            size="middle"
            icon={<ExportOutlined />}
            onClick={() => productId && dataModalRef.current?.open(productId, 'export')}
            disabled={checkPermissionDisabled('gateway:product:tsl:export')}
          >
            导出
          </Button>
          <Button
            size="middle"
            onClick={() => solutionCenterModalRef.current?.open()}
          >
            方案中心
          </Button>
        </div>
      </div>
      <Table
        dataSource={dataSource}
        rowKey="id"
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        bordered
        size="small"
        scroll={{ x: 'max-content' }}
      />
      <DataModal
        ref={dataModalRef}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
      <DetailModal
        ref={detailModalRef}
        productInfo={productInfo}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
      <SolutionCenterModal
        ref={solutionCenterModalRef}
        copyToProductId={productId}
        onSelect={() => {
          loadProductInfo();
        }}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
    </Card>
  );
};
